/**
 * What one run MEANS for the reader, beyond its bare status (PC6).
 *
 * A run's `status` says how the execution ended; for some runs the reader needs
 * more than that word. This file decides — from fields the server sent, and
 * nothing else — three things the list and the detail share:
 *
 *   - the BADGE (word + tone) a run carries;
 *   - a TITLE and a short TEXT for the detail's "Ergebnis";
 *   - a SUMMARY line for the history row.
 *
 * DRIVEN BY THE DATA, NEVER BY THE AUTOMATION'S KEY:
 *
 *   - `invoice_confirmation` — the confirmation a claim run recorded, read back
 *     from its ledger row by the server (PC6). Its `status` is the delivery
 *     truth: a run whose own row could not be closed still shows what the
 *     ledger recorded. `sending` is a claim whose outcome was never stored —
 *     never "is being sent": nothing local knows whether it went out, and
 *     nothing will ever retry it.
 *   - `target_type === "activation_baseline"` — the inventory a fresh
 *     activation took: nothing was sent, on purpose.
 *   - `target_type === "organization"` — a whole organisation's pass that
 *     stopped (before its first invoice, or — for a template or Bexio failure
 *     — in the middle of it); its code gets one explanatory sentence.
 *   - `target_type === "bexio_draft_request"` with a template code and no
 *     `invoice_confirmation` — one invoice's e-mail could not be rendered:
 *     nothing was sent, and the next run tries that invoice again.
 *
 * Every other run keeps the plain status word (`automationLabels`), and an
 * unknown ledger status falls back to it too — never to a guess.
 *
 * NO WORD PROMISES A RETRY, NO WORD CLAIMS A DELIVERY NOBODY CONFIRMED.
 * "Zustellung unklar" never says "nicht gesendet"; "Fehlgeschlagen" and the
 * unknown states say plainly that nothing is sent again automatically. The
 * amount and the period are the server's own strings — the e-mail's wording —
 * so this file parses no number.
 */
import {
  automationRunStatusLabel,
  automationRunStatusTone,
} from "./automationLabels";

const NO_RETRY = "Es erfolgt kein automatischer erneuter Versand.";

/** Keyed by the ledger status of `invoice_confirmation`. */
export const INVOICE_CONFIRMATION_OUTCOMES = {
  sent: {
    badge: "Gesendet",
    tone: "positive",
    title: "Zahlungsbestätigung gesendet",
    text: "",
    timeLabel: "Gesendet am",
    timeField: "sent_at",
  },
  failed: {
    badge: "Fehlgeschlagen",
    tone: "bad",
    title: "Versand fehlgeschlagen",
    text: `Die Nachricht konnte nicht versendet werden. ${NO_RETRY}`,
    timeLabel: "Fehlgeschlagen am",
    timeField: "failed_at",
  },
  indeterminate: {
    badge: "Zustellung unklar",
    tone: "warning",
    // Not the badge's words again: one status, said once. And no blame: the
    // attempt may have ended at the provider (timeout, 5xx) or before it (an
    // unknown transport error) — known is only that nothing confirmed it.
    title: "Zustellung nicht eindeutig bestätigt",
    text:
      "Es ist nicht bekannt, ob der E-Mail-Anbieter die Nachricht angenommen hat: " +
      `Der Versandversuch endete ohne eindeutige Bestätigung. ${NO_RETRY}`,
    timeLabel: "Als unklar erfasst am",
    timeField: "indeterminate_at",
  },
  sending: {
    // The status that is incomplete is the RECORD, not the message: the
    // provider may well have accepted it (a commit that failed afterwards).
    badge: "Versandstatus unvollständig",
    tone: "warning",
    title: "Versand nicht abschließend protokolliert",
    text:
      "Der Versand wurde vorgemerkt, sein Ergebnis aber nicht gespeichert. Ob die " +
      "Nachricht hinausging, ist nicht bekannt. Aus Sicherheitsgründen erfolgt " +
      "kein automatischer erneuter Versand.",
    timeLabel: "",
    timeField: "",
  },
};

// Every invoice that existed is kept from a confirmation for good — paid or
// not, and also when it is paid only later (PC5: a baseline row is final).
// Says what holds for the whole stock, not what THIS baseline recorded: true
// for a baseline that recorded nothing, and after a re-enable, where invoices
// an earlier cycle already recorded keep that record (the count's note says so).
const BASELINE = {
  badge: "Abgegrenzt",
  tone: "muted",
  title: "Historischer Bestand abgegrenzt",
  text:
    "Für alle zu diesem Zeitpunkt bestehenden Rechnungen werden keine " +
    "Zahlungsbestätigungen versendet – auch dann nicht, wenn sie erst später " +
    "vollständig bezahlt werden.",
};

// A render failure on one invoice's own run (PC5: before any claim). Nothing was
// sent and nothing was claimed, so this run did not settle the invoice — unlike
// a ledger failure, which is final. What happens to it later depends on things
// this row cannot know (the switch, the day's stop, a re-enable's baseline, the
// invoice itself), so the sentence speaks only of THIS run and forecasts none.
// It names no invoice: the run holds none (no ledger row exists before a
// claim). The cause is already the run's reason line ("… aus der Vorlage …").
const RENDER_FAILURE_TEXT =
  "In dieser Ausführung wurde nichts versendet und nichts vorgemerkt. Anders als " +
  "ein fehlgeschlagener Versand schließt das die Rechnung nicht ab.";
const RENDER_FAILURE_CODES = ["template_invalid", "template_render_failed"];

/**
 * One sentence per code of an organisation-wide stop: what it meant, and —
 * where the runner's contract says so — what it does NOT hold back. Only for
 * `target_type === "organization"`: a delivery code on an invoice's own run is
 * that invoice's outcome, explained by `invoice_confirmation`; a template code
 * there is a render failure (`RENDER_FAILURE_TEXT`).
 *
 * A run is read long after it was written, so no sentence here says "heute":
 * the day meant is the run's own (shown beside it). (The stop's short reason
 * label keeps the spec's own words, "Versand für heute angehalten" — read
 * beside the run's date, see `automationRunLabels`.) And no sentence claims
 * more than the runner knows — a day's stop follows ANY send of that day that
 * is not recorded as sent (failed, unclear, or never finally recorded); a
 * template or Bexio failure can stop a pass in the middle, after other
 * invoices went out; and no stop waits for a person to clear it. No sentence
 * forecasts a run (a switched-off automation runs no more): each says what
 * this run did and how long its stop lasts.
 */
export const ORGANIZATION_STOP_TEXTS = {
  send_stopped:
    "Ein Versand dieses Tages ist nicht abschließend als gesendet protokolliert. " +
    "Deshalb wurden für diese Organisation an diesem Tag (Zürcher Zeit) keine " +
    "weiteren Zahlungsbestätigungen versendet. Der Stopp gilt nur für diesen Tag.",
  bexio_auth_failed:
    "Bexio hat die gespeicherten Zugangsdaten abgelehnt. Ab diesem Zeitpunkt wurde " +
    "in diesem Lauf nichts mehr versendet; eine Sperre bleibt nicht zurück. Bitte " +
    "die Bexio-Verbindung prüfen.",
  bexio_connection_unavailable:
    "Die Bexio-Verbindung konnte nicht verwendet werden: Sie fehlt, ist nicht " +
    "aktiv, oder ihre Zugangsdaten sind nicht verfügbar. In diesem Lauf wurde " +
    "deshalb nichts geprüft und nichts versendet; eine Sperre bleibt nicht " +
    "zurück. Bitte die Bexio-Verbindung prüfen.",
  transport_not_configured:
    "Der E-Mail-Versand ist nicht vollständig konfiguriert. Es wurde nichts " +
    "versendet.",
  sender_config_missing:
    "Der Absender dieser Organisation ist nicht vollständig eingerichtet " +
    "(Name und E-Mail-Adresse). Es wurde nichts versendet.",
  template_invalid:
    "Die Vorlage konnte nicht verwendet werden. Ab diesem Zeitpunkt wurde in " +
    "diesem Lauf nichts mehr versendet.",
  template_render_failed:
    "Die Vorlage konnte nicht verwendet werden. Ab diesem Zeitpunkt wurde in " +
    "diesem Lauf nichts mehr versendet.",
};

const STOPPED_BADGE = { label: "Angehalten", tone: "warning" };

function statusBadge(status) {
  return { label: automationRunStatusLabel(status), tone: automationRunStatusTone(status) };
}

/** "RE-00123 · Oktober 2026 · CHF 1'490.00" — the server's strings, joined. */
export function invoiceConfirmationSummary(confirmation) {
  if (!confirmation) return "";
  return [
    confirmation.invoice_reference,
    confirmation.period_label,
    confirmation.amount_label,
  ]
    .filter(Boolean)
    .join(" · ");
}

/** The ledger outcome of a claim run, or `null` when it carries none we know. */
export function invoiceConfirmationOutcome(run) {
  const status = run?.invoice_confirmation?.status;
  return Object.prototype.hasOwnProperty.call(INVOICE_CONFIRMATION_OUTCOMES, status)
    ? INVOICE_CONFIRMATION_OUTCOMES[status]
    : null;
}

/**
 * The one line a ledger outcome may need about its RUN: a run whose own row
 * could not be closed after the outcome was stored (PC5 closes it in a
 * savepoint, and the ledger commit goes through regardless). The ledger is
 * the truth, so the generic "not finally confirmed" note never shows under a
 * ledger outcome — this says what actually stayed open. `sending` explains
 * itself, and a closed run needs nothing.
 */
export function invoiceConfirmationRunNote(run) {
  const status = run?.invoice_confirmation?.status;
  if (!invoiceConfirmationOutcome(run) || status === "sending" || run?.finished_at) return "";
  return (
    "Das Protokoll dieser Ausführung wurde nicht abgeschlossen; maßgeblich ist " +
    "der gespeicherte Versandstatus."
  );
}

/**
 * `{ badge: { label, tone }, title, text, summary }` for one run. Empty strings
 * mean "nothing to add": the caller shows what it showed before PC6.
 */
export function automationRunPresentation(run) {
  const outcome = invoiceConfirmationOutcome(run);
  if (outcome) {
    return {
      badge: { label: outcome.badge, tone: outcome.tone },
      title: outcome.title,
      text: outcome.text,
      summary: invoiceConfirmationSummary(run.invoice_confirmation),
    };
  }
  if (run?.target_type === "activation_baseline" && run?.status === "skipped") {
    return {
      badge: { label: BASELINE.badge, tone: BASELINE.tone },
      title: BASELINE.title,
      text: BASELINE.text,
      summary: "",
    };
  }
  if (
    run?.target_type === "bexio_draft_request" &&
    RENDER_FAILURE_CODES.includes(run?.error_code)
  ) {
    return { badge: statusBadge(run?.status), title: "", text: RENDER_FAILURE_TEXT, summary: "" };
  }
  if (run?.target_type === "organization") {
    const code = run?.error_code;
    const text = Object.prototype.hasOwnProperty.call(ORGANIZATION_STOP_TEXTS, code)
      ? ORGANIZATION_STOP_TEXTS[code]
      : "";
    return {
      badge: code === "send_stopped" ? STOPPED_BADGE : statusBadge(run?.status),
      title: "",
      text,
      summary: "",
    };
  }
  return { badge: statusBadge(run?.status), title: "", text: "", summary: "" };
}
